import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { Json } from '@ff-os/shared-json/Json';
import { calculateContainCropForFfCanvas } from '@ff-os/shared-utils/crop/calculateContainCropForFfCanvas';
import { calculateCoverCropForFfCanvas } from '@ff-os/shared-utils/crop/calculateCoverCropForFfCanvas';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { Photo } from 'ff-canvas';
import { canDisplayImage } from './canDisplayImage';
import { PhotoVariableInput } from './PhotoVariableInput';
import { PhotoVariableOptions } from './PhotoVariableOptions';
import { toPhotoUrl } from './toPhotoUrl';

export const photoToFfCanvas = (
    variable: GiftJson.Variable<PhotoVariableOptions>,
    input: PhotoVariableInput,
    palette: Json.Palette,
    target: PaintTarget,
    jwt: string | null
): Photo | Photo[] => {

    if (!canDisplayImage(input)) { // Photo placeholder
        if (target === 'print') {
            return []; // ignore
        } else {
            return createPhoto(variable, input, jwt); // show
        }
    }

    const imageSize = {
        width: input.imageWidth!,
        height: input.imageHeight!
    };

    const cropOverrides: Partial<Photo> = input.fit === 'contain'
        ? calculateContainCropForFfCanvas(variable, imageSize, input.cropBox!)
        : calculateCoverCropForFfCanvas(
            variable,
            imageSize,
            input.cropBox!,
            input.autoFaceCenter!,
            input.detections);

    return {
        ...createPhoto(variable, input, jwt),
        ...cropOverrides,
        ...toPrintOverrides(target)
    };
};

const createPhoto = (
    variable: GiftJson.Variable<PhotoVariableOptions>,
    input: PhotoVariableInput,
    jwt: string | null
): Photo => {

    return {

        id: variable.id,
        type: 'photo',
        round: false,

        url: canDisplayImage(input)
            ? toPhotoUrl(input, jwt)
            : null,

        x: variable.x,
        y: variable.y,
        width: variable.width,
        height: variable.height,
        zIndex: variable.zIndex,
        angle: 0,

        scaleX: 1,
        scaleY: 1,
        alpha: 1,

        filter: input.filter,
        brightness: 1,
        saturation: 1,
        contrast: 1,

        clipPaths: variable.options.clip?.paths ?? [],
        clipViewBoxWidth: variable.options.clip?.width ?? 0,
        clipViewBoxHeight: variable.options.clip?.height ?? 0,

        frameUrl: null,
        maskUrl: null,

        innerOffsetX: 0,
        innerOffsetY: 0,
        innerZoom: 1,
        innerAngle: 0,

        flipX: false,
        flipY: false,

        shadow: input.shadow,
        shadowBlur: input.shadowBlur,
        shadowColor: input.shadowColor,
        shadowOffsetX: input.shadowOffsetX,
        shadowOffsetY: input.shadowOffsetY,

        stroke: input.stroke,
        strokeSize: input.strokeSize,
        strokeStyle: input.strokeStyle,

        fit: input.fit,

        custom: {
            watch: [],
            values: {
                placeholder: {
                    x: variable.options.placeholderX,
                    y: variable.options.placeholderY,
                    fontSize: variable.options.placeholderFontSize
                }
            }
        }
    };
};

const toPrintOverrides = (target: PaintTarget): Partial<Photo> => {

    if (target === 'print') {
        return {
            svgOptions: {
                encoding: 'png',
                compression: 2
            }
        };
    }

    return {};
};